import { type ActivityOf, getItem } from '@wyn/content';
import { createRng } from '../rng.ts';
import { BTN, DT, type RealtimeSim, TICK_HZ } from './realtime.ts';
import { clamp, type Fx, lerp } from './types.ts';

const RAMP = 1.2, REST = 2.8, LEAD = 1.2;
const PAUSE_TICKS = Math.round(0.9 * TICK_HZ);
/** Racking any later than this past the peak and the ale has gone flat. */
const FLAT_AFTER = 3.2;
/** The rack gauge's gold band starts here. */
export const RACK_BAND = 0.85;
/** Each level above the brew widens the mash band by this much (°C), up to the cap. */
const BAND_PER_LEVEL = 0.05, BAND_MAX_BONUS = 1.5;

export interface BrewingSim extends RealtimeSim {
  phase: 'mash' | 'pause' | 'ferment';
  /** Seconds into the current phase. */
  time: number;
  /** Length of the mash schedule, seconds. */
  mashLength: number;
  /** Mash temperature, °C. */
  temp: number;
  vel: number;
  band: number;
  /** The gauge shows the gold band. Unmarked brews peak at their own height, so the turn is all there is to go on. */
  marked: boolean;
  rests: number[];
  /** The schedule's target temperature at `t` seconds into the mash. */
  targetAt(t: number): number;
  /** Fermentation activity now, 0–1; it peaks once. */
  activity: number;
  mash: number;
}

/**
 * The Brewhouse: hold to stoke the fire and follow the mash schedule as it steps up, then rack the
 * ale (press) while fermentation is at its fiercest. Practice beyond the brew widens the band. From
 * the porter up the gauge is unmarked and each batch peaks at its own height. Ported from the
 * Harrowmere prototype.
 */
export function brewingSim(a: ActivityOf<'brewing'>, level: number, seed: string): BrewingSim {
  const rng = createRng(seed);
  const rand = (lo: number, hi: number) => lo + rng.next() * (hi - lo);
  const rests: number[] = [];
  let tt = rand(44, 50);
  for (let i = 0; i < a.steps; i++) {
    rests.push(Math.min(79, tt));
    tt += rand(5, 9);
  }
  const T = LEAD + a.steps * REST + (a.steps - 1) * RAMP;
  const targetAt = (t: number) => {
    if (t < LEAD) return rests[0]!;
    let s = LEAD;
    for (let i = 0; i < rests.length; i++) {
      if (i > 0) {
        if (t < s + RAMP) return lerp(rests[i - 1]!, rests[i]!, (t - s) / RAMP);
        s += RAMP;
      }
      if (t < s + REST) return rests[i]!;
      s += REST;
    }
    return rests[rests.length - 1]!;
  };
  const peak = rand(3, 5.5), w = 1.1 - a.steps * 0.08;
  const height = a.unmarked ? rand(0.55, 1) : 1;
  const act = (t: number) => height * Math.exp(-((t - peak) ** 2) / (2 * w * w));
  const band = a.band + Math.min(BAND_MAX_BONUS, Math.max(0, level - a.level) * BAND_PER_LEVEL);
  const product = getItem(a.product).name.toLowerCase();

  let prev = 0, sum = 0, n = 0, pause = 0;
  const fx = (text: string, tone: Fx['tone'], cue?: string) => sim.fx.push({ tick: sim.tick, text, tone, cue });
  const end = (ferment: number, flat = false) => {
    const score = sim.mash * 0.7 + ferment * 0.3;
    sim.result = {
      score,
      title: `You rack a ${product}`,
      text: score > 0.75 ? 'The brewster tastes it and smiles.' : score > 0.45 ? 'Drinkable, which is all most folk ask.' : 'Thin and sour. Someone will drink it.',
      breakdown: [['Mash schedule', sim.mash], ['Racking', ferment]],
      yield: { itemKey: a.product, quantity: 1, graded: true },
      coin: 0,
      xpMult: 1,
      feats: flat ? ['gone-flat'] : ferment > 0.85 ? ['perfect-rack'] : [],
    };
  };

  const sim: BrewingSim = {
    tick: 0,
    result: null,
    fx: [],
    phase: 'mash',
    time: 0,
    mashLength: T,
    temp: rests[0]! - 7,
    vel: 0,
    band,
    marked: !a.unmarked,
    rests,
    targetAt,
    activity: 0,
    mash: 0,
    step(mask) {
      if (sim.result) return;
      const held = (mask & BTN.main) !== 0;
      const pressed = held && !(prev & BTN.main);
      prev = mask;
      sim.tick++;
      if (sim.phase === 'mash') {
        sim.time += DT;
        sim.vel += ((held ? 11 : -5.5) - sim.vel) * Math.min(1, DT * 2.2);
        sim.temp = clamp(sim.temp + sim.vel * DT, 30, 90);
        const d = Math.abs(sim.temp - targetAt(sim.time));
        if (sim.time > LEAD) {
          sum += (d <= band ? 1 - (d / band) * 0.5 : 0) * DT;
          n += DT;
        }
        if (sim.time >= T) {
          sim.mash = n ? sum / n : 0;
          fx(sim.mash > 0.75 ? 'A clean mash' : sim.mash > 0.45 ? 'A fair mash' : 'A scorched, patchy mash', sim.mash > 0.45 ? 'good' : 'bad', 'mashed');
          sim.phase = 'pause';
          pause = PAUSE_TICKS;
        }
      } else if (sim.phase === 'pause') {
        if (--pause <= 0) {
          sim.phase = 'ferment';
          sim.time = 0;
        }
      } else {
        if (pressed) {
          // Judged against this batch's own peak.
          const v = act(sim.time) / height;
          fx(v > 0.85 ? 'Perfectly racked!' : v > 0.5 ? 'Racked a touch off' : 'Badly timed', v > 0.85 ? 'good' : 'gold', 'racked');
          end(v);
          return;
        }
        sim.time += DT;
        sim.activity = act(sim.time);
        if (sim.time > peak + FLAT_AFTER) {
          fx('The ale has gone flat', 'bad', 'flat');
          end(0.15, true);
        }
      }
    },
  };
  return sim;
}
